// src/components/sharedCollection/SharedCollectionPublicCommentsPanel.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
   Box,
   Typography,
   Button,
   TextField,
   Avatar,
   CircularProgress,
   Alert,
   Dialog,
   DialogTitle,
   DialogContent,
   IconButton,
   Menu,
   MenuItem,
} from '@mui/material';
import {
   ChatBubbleOutline as ChatBubbleOutlineIcon,
   Close as CloseIcon,
   SendRounded as SendRoundedIcon,
   Favorite as FavoriteIcon,
   FavoriteBorder as FavoriteBorderIcon,
   MoreHoriz as MoreHorizIcon,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { formatDistanceToNow } from 'date-fns';
import WetrippenAuthPromoDialog from './WetrippenAuthPromoDialog';
import SharedCollectionService from '../../services/sharedCollectionService';
import type { SharedCollectionPublicComment, SharedPhoto } from '../../types/SharedCollection';
import { isSharedVideoPhoto, videoPlaybackSrc } from '../../utils/sharedCollectionMedia';

const MAX_LEN = 2000;
const REACTION_EMOJIS = ['👍', '❤️', '😂', '🔥', '😮', '🙏'] as const;

/** Tagged-photo chip in comment header (slightly larger for tap targets). */
const TAG_THUMB_SIZE = { xs: 40, sm: 48 } as const;

export type CommentTreeNode = SharedCollectionPublicComment & { replies: CommentTreeNode[] };

function buildCommentTree(flat: SharedCollectionPublicComment[]): CommentTreeNode[] {
   const byId = new Map<string, CommentTreeNode>();
   flat.forEach((c) => {
      byId.set(c.id, {
         ...c,
         replies: [],
      });
   });
   const roots: CommentTreeNode[] = [];
   flat.forEach((c) => {
      const node = byId.get(c.id)!;
      const pid = c.parentCommentId || null;
      if (pid && byId.has(pid)) {
         byId.get(pid)!.replies.push(node);
      } else {
         roots.push(node);
      }
   });
   const desc = (a: SharedCollectionPublicComment, b: SharedCollectionPublicComment) =>
      (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0);
   const asc = (a: SharedCollectionPublicComment, b: SharedCollectionPublicComment) =>
      (a.createdAt?.getTime() ?? 0) - (b.createdAt?.getTime() ?? 0);
   roots.sort(desc);
   const sortReplies = (nodes: CommentTreeNode[]) => {
      nodes.forEach((n) => {
         n.replies.sort(asc);
         sortReplies(n.replies);
      });
   };
   sortReplies(roots);
   return roots;
}

type CommentThreadProps = {
   node: CommentTreeNode;
   depth: number;
   theme: Theme;
   thumbForComment: (photoOriginalId: string | null) => string | null;
   onReply: (c: SharedCollectionPublicComment) => void;
   /** Opens enlarged preview (and may fall back to parent lightbox if photo not in list). */
   onOpenTaggedPhoto?: (photoOriginalId: string) => void;
   likeBusyCommentId: string | null;
   reactionBusyCommentId: string | null;
   onToggleLike: (commentId: string) => void;
   onPickReaction: (commentId: string, emoji: string) => void;
};

const CommentThread: React.FC<CommentThreadProps> = ({
   node,
   depth,
   theme,
   thumbForComment,
   onReply,
   onOpenTaggedPhoto,
   likeBusyCommentId,
   reactionBusyCommentId,
   onToggleLike,
   onPickReaction,
}) => {
   const thumb = thumbForComment(node.photoOriginalId);
   const likeCount = node.likeCount ?? 0;
   const viewerHasLiked = Boolean(node.viewerHasLiked);
   const reactionCounts = node.reactionCounts ?? {};
   const viewerReactions =
      node.viewerReactions && node.viewerReactions.length > 0
         ? node.viewerReactions
         : node.viewerReaction
           ? [node.viewerReaction]
           : [];
   const likeBusy = likeBusyCommentId === node.id;
   const reactionBusy = reactionBusyCommentId === node.id;
   const [emojiMenuAnchor, setEmojiMenuAnchor] = useState<null | HTMLElement>(null);
   const totalReactionCount = REACTION_EMOJIS.reduce((acc, em) => acc + (reactionCounts[em] ?? 0), 0);

   useEffect(() => {
      if (reactionBusy) setEmojiMenuAnchor(null);
   }, [reactionBusy]);

   return (
      <Box>
         <Box
            display='flex'
            gap={1.5}
            sx={{
               py: 2,
               pl: depth > 0 ? 0.5 : 0,
            }}
         >
            <Avatar
               src={node.authorPhotoURL ? String(node.authorPhotoURL).trim() : undefined}
               alt={node.displayName || 'Comment author'}
               imgProps={{ referrerPolicy: 'no-referrer' }}
               sx={{
                  width: depth > 0 ? 34 : 40,
                  height: depth > 0 ? 34 : 40,
                  fontSize: 12,
                  fontWeight: 700,
                  bgcolor: 'action.hover',
                  color: 'text.secondary',
                  flexShrink: 0,
                  '& img': { objectFit: 'cover' },
               }}
            >
               {!node.authorPhotoURL
                  ? (node.displayName || 'U').slice(0, 2).toUpperCase()
                  : undefined}
            </Avatar>
            <Box flex={1} minWidth={0}>
               <Box display='flex' alignItems='center' gap={1} flexWrap='wrap' rowGap={0.5}>
                  <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                     {node.displayName}
                  </Typography>
                  <Typography variant='caption' color='text.secondary' component='span'>
                     {node.createdAt ? formatDistanceToNow(node.createdAt, { addSuffix: true }) : ''}
                  </Typography>
                  {node.photoOriginalId && onOpenTaggedPhoto ? (
                     <Box
                        component='button'
                        type='button'
                        onClick={() => onOpenTaggedPhoto(node.photoOriginalId!)}
                        sx={{
                           display: 'inline-flex',
                           alignItems: 'center',
                           gap: 1,
                           p: 0,
                           m: 0,
                           border: 'none',
                           background: 'none',
                           cursor: 'pointer',
                           color: 'primary.light',
                           font: 'inherit',
                           borderRadius: 1,
                           textAlign: 'left',
                           maxWidth: '100%',
                           '&:hover': {
                              opacity: 0.92,
                              '& .tag-thumb': {
                                 boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.35)}`,
                              },
                           },
                        }}
                        aria-label='Open tagged photo in a larger view'
                     >
                        {thumb ? (
                           <Box
                              component='img'
                              src={thumb}
                              alt=''
                              className='tag-thumb'
                              sx={{
                                 width: TAG_THUMB_SIZE,
                                 height: TAG_THUMB_SIZE,
                                 borderRadius: 1,
                                 objectFit: 'cover',
                                 flexShrink: 0,
                                 display: 'block',
                                 border: `1px solid ${alpha(theme.palette.divider, 0.45)}`,
                                 boxShadow: `0 1px 6px ${alpha(theme.palette.common.black, 0.28)}`,
                              }}
                           />
                        ) : null}
                        <Typography variant='caption' component='span' fontWeight={600}>
                           View photo
                        </Typography>
                     </Box>
                  ) : node.photoOriginalId ? (
                     <Box display='inline-flex' alignItems='center' gap={1}>
                        {thumb ? (
                           <Box
                              component='img'
                              src={thumb}
                              alt=''
                              sx={{
                                 width: TAG_THUMB_SIZE,
                                 height: TAG_THUMB_SIZE,
                                 borderRadius: 1,
                                 objectFit: 'cover',
                                 flexShrink: 0,
                                 display: 'block',
                                 border: `1px solid ${alpha(theme.palette.divider, 0.45)}`,
                              }}
                           />
                        ) : null}
                        <Typography variant='caption' color='text.secondary' component='span' fontWeight={500}>
                           On this photo
                        </Typography>
                     </Box>
                  ) : null}
               </Box>
               <Typography variant='body2' sx={{ mt: 0.75, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
                  {node.text}
               </Typography>
               {totalReactionCount > 0 ? (
                  <Box
                     display='flex'
                     flexWrap='wrap'
                     alignItems='center'
                     gap={0.5}
                     sx={{ mt: 0.75 }}
                     aria-label='Reactions on this comment'
                  >
                     {REACTION_EMOJIS.map((em) => {
                        const count = reactionCounts[em] ?? 0;
                        if (!count) return null;
                        const mine = viewerReactions.includes(em);
                        return (
                           <Box
                              key={em}
                              component='button'
                              type='button'
                              disabled={reactionBusy}
                              aria-pressed={mine}
                              aria-label={`Toggle ${em} reaction, ${count} total`}
                              onClick={() => onPickReaction(node.id, em)}
                              sx={{
                                 display: 'inline-flex',
                                 alignItems: 'center',
                                 gap: 0.35,
                                 py: 0.25,
                                 px: 0.85,
                                 borderRadius: 999,
                                 m: 0,
                                 font: 'inherit',
                                 cursor: reactionBusy ? 'default' : 'pointer',
                                 bgcolor: mine
                                    ? alpha(theme.palette.primary.main, 0.16)
                                    : alpha(theme.palette.common.white, 0.06),
                                 border: mine
                                    ? `1px solid ${alpha(theme.palette.primary.main, 0.45)}`
                                    : `1px solid ${alpha(theme.palette.divider, 0.35)}`,
                                 color: 'inherit',
                                 '&:hover:not(:disabled)': {
                                    bgcolor: mine
                                       ? alpha(theme.palette.primary.main, 0.22)
                                       : alpha(theme.palette.common.white, 0.1),
                                 },
                                 '&:focus-visible': {
                                    outline: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                                    outlineOffset: 2,
                                 },
                                 '&:disabled': { opacity: 0.55, cursor: 'default' },
                              }}
                           >
                              <Typography component='span' sx={{ fontSize: '1rem', lineHeight: 1 }}>
                                 {em}
                              </Typography>
                              <Typography variant='caption' color='text.secondary' fontWeight={700}>
                                 {count}
                              </Typography>
                           </Box>
                        );
                     })}
                  </Box>
               ) : null}
               <Box
                  display='flex'
                  alignItems='center'
                  flexWrap='wrap'
                  gap={0.25}
                  rowGap={0.5}
                  sx={{ mt: 0.75 }}
               >
                  <IconButton
                     size='small'
                     aria-label={viewerHasLiked ? 'Unlike comment' : 'Like comment'}
                     aria-busy={likeBusy}
                     disabled={likeBusy}
                     onClick={() => onToggleLike(node.id)}
                     sx={{
                        color: viewerHasLiked ? 'error.main' : 'text.secondary',
                        '&:hover': { color: viewerHasLiked ? 'error.light' : 'text.primary' },
                     }}
                  >
                     {likeBusy ? (
                        <CircularProgress size={16} thickness={5} sx={{ color: 'inherit' }} />
                     ) : viewerHasLiked ? (
                        <FavoriteIcon sx={{ fontSize: 18, color: 'inherit' }} />
                     ) : (
                        <FavoriteBorderIcon sx={{ fontSize: 18, color: 'inherit' }} />
                     )}
                  </IconButton>
                  {likeCount > 0 ? (
                     <Typography variant='caption' color='text.secondary' component='span' sx={{ mr: 0.5 }}>
                        {likeCount}
                     </Typography>
                  ) : null}
                  <IconButton
                     size='small'
                     aria-label='Open reactions'
                     aria-haspopup='true'
                     aria-expanded={Boolean(emojiMenuAnchor)}
                     aria-busy={reactionBusy}
                     disabled={reactionBusy}
                     onClick={(e) => {
                        if (reactionBusy) return;
                        setEmojiMenuAnchor(e.currentTarget);
                     }}
                     sx={{
                        color: viewerReactions.length > 0 ? 'primary.main' : 'text.secondary',
                        ml: 0.25,
                        width: 34,
                        height: 34,
                     }}
                  >
                     {reactionBusy ? (
                        <CircularProgress size={18} thickness={4} sx={{ color: 'inherit' }} />
                     ) : (
                        <MoreHorizIcon sx={{ fontSize: 20 }} />
                     )}
                  </IconButton>
                  <Menu
                     anchorEl={emojiMenuAnchor}
                     open={Boolean(emojiMenuAnchor) && !reactionBusy}
                     onClose={() => setEmojiMenuAnchor(null)}
                     anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                     transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                     slotProps={{
                        paper: {
                           sx: {
                              mt: 0.5,
                              borderRadius: 1.5,
                              bgcolor: alpha(theme.palette.background.paper, 0.98),
                              border: `1px solid ${alpha(theme.palette.divider, 0.25)}`,
                              py: 0.5,
                              minWidth: 0,
                           },
                        },
                     }}
                  >
                     {REACTION_EMOJIS.map((em) => {
                        const selected = viewerReactions.includes(em);
                        return (
                           <MenuItem
                              key={em}
                              dense
                              disabled={reactionBusy}
                              selected={selected}
                              onClick={() => {
                                 setEmojiMenuAnchor(null);
                                 onPickReaction(node.id, em);
                              }}
                              sx={{
                                 fontSize: '1.2rem',
                                 minHeight: 40,
                                 justifyContent: 'center',
                                 px: 2,
                                 minWidth: 48,
                              }}
                           >
                              <span aria-hidden>{em}</span>
                           </MenuItem>
                        );
                     })}
                  </Menu>
                  <Button
                     size='small'
                     variant='text'
                     sx={{
                        ml: 0.5,
                        p: 0,
                        minWidth: 0,
                        textTransform: 'none',
                        fontWeight: 600,
                        color: 'primary.light',
                     }}
                     onClick={() => onReply(node)}
                  >
                     Reply
                  </Button>
               </Box>
            </Box>
         </Box>
         {node.replies.length > 0 ? (
            <Box
               sx={{
                  pl: { xs: 1.5, sm: 2.25 },
                  ml: 0.5,
               }}
            >
               {node.replies.map((child) => (
                  <CommentThread
                     key={child.id}
                     node={child}
                     depth={depth + 1}
                     theme={theme}
                     thumbForComment={thumbForComment}
                     onReply={onReply}
                     onOpenTaggedPhoto={onOpenTaggedPhoto}
                     likeBusyCommentId={likeBusyCommentId}
                     reactionBusyCommentId={reactionBusyCommentId}
                     onToggleLike={onToggleLike}
                     onPickReaction={onPickReaction}
                  />
               ))}
            </Box>
         ) : null}
      </Box>
   );
};

export interface SharedCollectionPublicCommentsPanelProps {
   shareToken: string;
   /** Opens collection lightbox for a photo tagged on a comment. */
   onOpenTaggedPhoto?: (photoOriginalId: string) => void;
   currentUser: {
      uid: string;
      displayName?: string | null;
      photoURL?: string | null;
      email?: string | null;
   } | null;
   displayPhotos: SharedPhoto[];
   activePhotoOriginalId: string | null;
   onActivePhotoOriginalIdChange: (photoOriginalId: string | null) => void;
   /** Increment from parent (e.g. after comment icon click) to focus the composer. */
   focusComposerSignal?: number;
   onNavigateLogin: () => void;
   onNavigateRegister: () => void;
   onCommentsChange: (comments: SharedCollectionPublicComment[]) => void;
}

function initialsFromUser(user: SharedCollectionPublicCommentsPanelProps['currentUser']): string {
   if (!user) return '?';
   const name = (user.displayName || user.email || 'Me').trim();
   const parts = name.split(/\s+/).filter(Boolean);
   if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
   }
   return name.slice(0, 2).toUpperCase() || 'ME';
}

const SharedCollectionPublicCommentsPanel: React.FC<SharedCollectionPublicCommentsPanelProps> = ({
   shareToken,
   onOpenTaggedPhoto,
   currentUser,
   displayPhotos,
   activePhotoOriginalId,
   onActivePhotoOriginalIdChange,
   focusComposerSignal = 0,
   onNavigateLogin,
   onNavigateRegister,
   onCommentsChange,
}) => {
   const theme = useTheme();
   const [comments, setComments] = useState<SharedCollectionPublicComment[]>([]);
   const [loading, setLoading] = useState(true);
   const [loadError, setLoadError] = useState<string | null>(null);
   const [draft, setDraft] = useState('');
   const [posting, setPosting] = useState(false);
   const [postError, setPostError] = useState<string | null>(null);
   const [authDialogOpen, setAuthDialogOpen] = useState(false);
   const [replyingTo, setReplyingTo] = useState<{ id: string; displayName: string } | null>(null);
   const [likeBusyCommentId, setLikeBusyCommentId] = useState<string | null>(null);
   const [reactionBusyCommentId, setReactionBusyCommentId] = useState<string | null>(null);
   /** Full-screen preview for a tagged photo (comment chip or composer context). */
   const [tagPreviewPhoto, setTagPreviewPhoto] = useState<SharedPhoto | null>(null);
   const inputRef = useRef<HTMLTextAreaElement | null>(null);
   const onCommentsChangeRef = useRef(onCommentsChange);
   onCommentsChangeRef.current = onCommentsChange;

   const requireAuth = useCallback(() => {
      if (!currentUser) {
         setAuthDialogOpen(true);
         return true;
      }
      return false;
   }, [currentUser]);

   useEffect(() => {
      if (currentUser) setAuthDialogOpen(false);
   }, [currentUser]);

   useEffect(() => {
      if (!activePhotoOriginalId) return;
      const stillVisible = displayPhotos.some((p) => p.originalPhotoId === activePhotoOriginalId);
      if (!stillVisible) onActivePhotoOriginalIdChange(null);
   }, [displayPhotos, activePhotoOriginalId, onActivePhotoOriginalIdChange]);

   useEffect(() => {
      if (!shareToken) return;
      let cancelled = false;
      (async () => {
         setLoading(true);
         setLoadError(null);
         try {
            const list = await SharedCollectionService.getPublicComments(shareToken);
            if (!cancelled) {
               setComments(list);
               onCommentsChangeRef.current(list);
            }
         } catch (e: any) {
            if (!cancelled) {
               setLoadError(e?.message || 'Could not load comments');
               setComments([]);
               onCommentsChangeRef.current([]);
            }
         } finally {
            if (!cancelled) setLoading(false);
         }
      })();
      return () => {
         cancelled = true;
      };
   }, [shareToken]);

   useEffect(() => {
      if (!focusComposerSignal) return;
      if (requireAuth()) return;
      inputRef.current?.focus();
   }, [focusComposerSignal, requireAuth]);

   const activePhoto = useMemo(
      () => displayPhotos.find((p) => p.originalPhotoId === activePhotoOriginalId) || null,
      [displayPhotos, activePhotoOriginalId],
   );

   /** Merge viewer auth photo/name when API row is missing them (e.g. legacy comments). */
   const commentsEnrichedWithViewerProfile = useMemo(() => {
      if (!currentUser?.uid) return comments;
      return comments.map((c) => {
         if (c.userId !== currentUser.uid) return c;
         const placeholderName =
            !c.displayName || c.displayName === 'Member' || c.displayName === 'Someone';
         return {
            ...c,
            authorPhotoURL: c.authorPhotoURL || currentUser.photoURL || null,
            displayName: placeholderName
               ? (currentUser.displayName && String(currentUser.displayName).trim()) || c.displayName
               : c.displayName,
         };
      });
   }, [comments, currentUser?.uid, currentUser?.photoURL, currentUser?.displayName]);

   const commentTree = useMemo(
      () => buildCommentTree(commentsEnrichedWithViewerProfile),
      [commentsEnrichedWithViewerProfile],
   );

   const thumbForComment = useCallback(
      (photoOriginalId: string | null) => {
         if (!photoOriginalId) return null;
         const p = displayPhotos.find((x) => x.originalPhotoId === photoOriginalId);
         if (!p) return null;
         return p.thumbnailUrl || p.url || null;
      },
      [displayPhotos],
   );

   const handleOpenTaggedPhotoPreview = useCallback(
      (photoOriginalId: string) => {
         const id = String(photoOriginalId || '').trim();
         if (!id) return;
         const p = displayPhotos.find((x) => x.originalPhotoId === id);
         if (p) {
            setTagPreviewPhoto(p);
            return;
         }
         onOpenTaggedPhoto?.(photoOriginalId);
      },
      [displayPhotos, onOpenTaggedPhoto],
   );

   const handleToggleLike = useCallback(
      async (commentId: string) => {
         if (requireAuth()) return;
         setLikeBusyCommentId(commentId);
         try {
            const { likeCount, viewerHasLiked } = await SharedCollectionService.togglePublicCommentLike(
               shareToken,
               commentId,
            );
            setComments((prev) => {
               const next = prev.map((c) =>
                  c.id === commentId ? { ...c, likeCount, viewerHasLiked } : c,
               );
               onCommentsChangeRef.current(next);
               return next;
            });
         } catch (e) {
            console.warn('togglePublicCommentLike', e);
         } finally {
            setLikeBusyCommentId(null);
         }
      },
      [requireAuth, shareToken],
   );

   const handlePickReaction = useCallback(
      async (commentId: string, emoji: string) => {
         if (requireAuth()) return;
         setReactionBusyCommentId(commentId);
         try {
            const { reactionCounts, viewerReaction, viewerReactions } =
               await SharedCollectionService.setPublicCommentReaction(shareToken, commentId, emoji);
            setComments((prev) => {
               const next = prev.map((c) =>
                  c.id === commentId
                     ? { ...c, reactionCounts, viewerReaction, viewerReactions }
                     : c,
               );
               onCommentsChangeRef.current(next);
               return next;
            });
         } catch (e) {
            console.warn('setPublicCommentReaction', e);
         } finally {
            setReactionBusyCommentId(null);
         }
      },
      [requireAuth, shareToken],
   );

   const handlePost = async () => {
      if (requireAuth()) return;
      const text = draft.trim();
      if (!text) {
         setPostError('Write something first.');
         return;
      }
      setPostError(null);
      setPosting(true);
      try {
         const tagId = replyingTo ? undefined : activePhoto?.originalPhotoId?.trim() || undefined;
         const created = await SharedCollectionService.postPublicComment(shareToken, {
            text,
            ...(tagId ? { photoOriginalId: tagId } : {}),
            ...(replyingTo ? { parentCommentId: replyingTo.id } : {}),
         });
         const createdWithAvatar: typeof created = {
            ...created,
            authorPhotoURL: created.authorPhotoURL || currentUser?.photoURL || null,
            likeCount: created.likeCount ?? 0,
            viewerHasLiked: created.viewerHasLiked ?? false,
            reactionCounts: created.reactionCounts ?? {},
            viewerReaction: created.viewerReaction ?? null,
            viewerReactions: created.viewerReactions ?? [],
         };
         setComments((prev) => {
            const next = [createdWithAvatar, ...prev];
            onCommentsChangeRef.current(next);
            return next;
         });
         setDraft('');
         setReplyingTo(null);
         onActivePhotoOriginalIdChange(null);
      } catch (e: any) {
         setPostError(e?.message || 'Failed to post');
      } finally {
         setPosting(false);
      }
   };

   const composerPlaceholder = replyingTo
      ? `Reply to ${replyingTo.displayName}…`
      : activePhoto
        ? 'What do you think of this photo?'
        : 'What do you think of this collection?';

   return (
      <Box
         sx={{
            height: '100%',
            width: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            color: 'text.primary',
            px: { xs: 0.5, sm: 1 },
            pt: 0.5,
         }}
      >
         <Box display='flex' alignItems='center' gap={1.5} mb={2.5} flexShrink={0}>
            <Box
               sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(theme.palette.common.white, 0.06),
                  color: 'text.secondary',
               }}
            >
               <ChatBubbleOutlineIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box flex={1} minWidth={0}>
               <Typography
                  variant='h6'
                  component='h2'
                  sx={{ fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}
               >
                  Comments
               </Typography>
               <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.25 }}>
                  Share your thoughts on this collection
               </Typography>
            </Box>
         </Box>

         {loadError && (
            <Alert severity='error' sx={{ mb: 2, flexShrink: 0, borderRadius: 1 }}>
               {loadError}
            </Alert>
         )}

         <Box
            sx={{
               pt: 0.5,
               pb: 2,
               mb: 2,
               flexShrink: 0,
            }}
         >
            {replyingTo && (
               <Box display='flex' alignItems='center' gap={1} mb={1.5} flexWrap='wrap'>
                  <Typography variant='caption' color='text.secondary' fontWeight={600}>
                     Replying to {replyingTo.displayName}
                  </Typography>
                  <IconButton
                     size='small'
                     aria-label='Cancel reply'
                     onClick={() => setReplyingTo(null)}
                     sx={{ color: 'text.secondary', ml: 'auto' }}
                  >
                     <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
               </Box>
            )}
            {activePhotoOriginalId && activePhoto && (
               <Box display='flex' alignItems='center' gap={1} mb={1.5} flexWrap='wrap'>
                  <IconButton
                     size='small'
                     onClick={() => setTagPreviewPhoto(activePhoto)}
                     aria-label='Open photo preview'
                     sx={{
                        p: 0,
                        borderRadius: 1,
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.45)}`,
                        boxShadow: (t) => `0 1px 6px ${alpha(t.palette.common.black, 0.22)}`,
                        '&:hover': {
                           boxShadow: (t) => `0 2px 12px ${alpha(t.palette.common.black, 0.3)}`,
                        },
                     }}
                  >
                     <Box
                        component='img'
                        src={activePhoto.thumbnailUrl || activePhoto.url}
                        alt=''
                        sx={{
                           width: TAG_THUMB_SIZE,
                           height: TAG_THUMB_SIZE,
                           borderRadius: 1,
                           objectFit: 'cover',
                           display: 'block',
                        }}
                     />
                  </IconButton>
                  <Typography variant='caption' color='text.secondary' fontWeight={600}>
                     Commenting on this photo
                  </Typography>
                  <IconButton
                     size='small'
                     sx={{
                        color: 'text.secondary',
                        ml: 'auto',
                        flexShrink: 0,
                        '&:hover': { color: 'primary.light', bgcolor: alpha(theme.palette.primary.main, 0.08) },
                     }}
                     aria-label='Clear photo'
                     onClick={() => onActivePhotoOriginalIdChange(null)}
                  >
                     <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
               </Box>
            )}

            <Box display='flex' gap={1.75} alignItems='flex-start'>
               <Avatar
                  src={currentUser?.photoURL || undefined}
                  sx={{
                     width: 42,
                     height: 42,
                     fontSize: 15,
                     fontWeight: 700,
                     bgcolor: 'action.hover',
                     color: 'text.secondary',
                  }}
               >
                  {initialsFromUser(currentUser)}
               </Avatar>
               <Box flex={1} minWidth={0}>
                  <TextField
                     inputRef={inputRef}
                     fullWidth
                     multiline
                     minRows={2}
                     maxRows={6}
                     placeholder={composerPlaceholder}
                     value={draft}
                     onChange={(e) => setDraft(e.target.value.slice(0, MAX_LEN))}
                     onFocus={() => requireAuth()}
                     onClick={() => requireAuth()}
                     InputProps={{
                        readOnly: !currentUser,
                     }}
                     sx={{
                        '& .MuiOutlinedInput-root': {
                           borderRadius: 1.5,
                           bgcolor: alpha(theme.palette.common.black, 0.22),
                           color: 'text.primary',
                           '& fieldset': {
                              border: 'none',
                           },
                           '&:hover fieldset': {
                              border: 'none',
                           },
                           '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                           },
                           '&.Mui-focused': {
                              bgcolor: alpha(theme.palette.common.black, 0.3),
                           },
                        },
                        '& .MuiInputBase-input::placeholder': {
                           color: 'text.secondary',
                           opacity: 1,
                        },
                     }}
                  />
                  {postError && (
                     <Typography variant='caption' color='error' sx={{ mt: 0.75, display: 'block' }}>
                        {postError}
                     </Typography>
                  )}
                  <Button
                     variant='contained'
                     color='primary'
                     size='medium'
                     disableElevation
                     startIcon={
                        posting ? undefined : <SendRoundedIcon sx={{ fontSize: 18 }} />
                     }
                     sx={{
                        mt: 1.5,
                        alignSelf: 'flex-end',
                        px: 2.75,
                        py: 1,
                        borderRadius: 1.5,
                        fontWeight: 700,
                        textTransform: 'none',
                        letterSpacing: '0.03em',
                        boxShadow: 'none',
                        '&:hover': {
                           boxShadow: 'none',
                           transform: 'none',
                        },
                        '&:active': { transform: 'none' },
                        '&.Mui-disabled': {
                           bgcolor: alpha(theme.palette.primary.main, 0.25),
                           color: alpha(theme.palette.common.black, 0.35),
                        },
                        transition: 'background-color 0.2s ease',
                     }}
                     disabled={posting || !draft.trim()}
                     onClick={() => {
                        if (requireAuth()) return;
                        handlePost();
                     }}
                  >
                     {posting ? <CircularProgress size={22} sx={{ color: 'primary.contrastText' }} /> : 'Post'}
                  </Button>
               </Box>
            </Box>
         </Box>

         <Box
            sx={{
               flex: 1,
               minHeight: 0,
               overflowY: 'auto',
               overflowX: 'hidden',
               pr: 0.5,
               mr: -0.5,
            }}
         >
            {loading ? (
               <Box display='flex' justifyContent='center' alignItems='center' minHeight={160}>
                  <CircularProgress size={36} thickness={4} sx={{ color: 'primary.main' }} />
               </Box>
            ) : (
               <Box display='flex' flexDirection='column' gap={0}>
                  {comments.length === 0 && (
                     <Box
                        sx={(t) => ({
                           py: 3,
                           px: 2,
                           textAlign: 'center',
                           borderRadius: 1,
                           bgcolor: alpha(t.palette.common.white, 0.03),
                        })}
                     >
                        <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.6 }}>
                           No comments yet.
                           <Box component='span' sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                              Start the conversation.
                           </Box>
                        </Typography>
                     </Box>
                  )}
                  {commentTree.map((node) => (
                     <CommentThread
                        key={node.id}
                        node={node}
                        depth={0}
                        theme={theme}
                        thumbForComment={thumbForComment}
                        onReply={(c) => {
                           if (requireAuth()) return;
                           setReplyingTo({ id: c.id, displayName: c.displayName || 'Member' });
                           inputRef.current?.focus();
                        }}
                        onOpenTaggedPhoto={handleOpenTaggedPhotoPreview}
                        likeBusyCommentId={likeBusyCommentId}
                        reactionBusyCommentId={reactionBusyCommentId}
                        onToggleLike={handleToggleLike}
                        onPickReaction={handlePickReaction}
                     />
                  ))}
               </Box>
            )}
         </Box>

         <Dialog
            open={tagPreviewPhoto !== null}
            onClose={() => setTagPreviewPhoto(null)}
            maxWidth='md'
            fullWidth
            aria-labelledby='tagged-photo-preview-title'
            PaperProps={{
               sx: {
                  borderRadius: 2,
                  bgcolor: 'background.default',
                  overflow: 'hidden',
               },
            }}
         >
            <DialogTitle
               id='tagged-photo-preview-title'
               sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  py: 1.5,
                  px: 2,
                  pr: 1,
               }}
            >
               <Typography variant='subtitle1' component='span' fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>
                  {tagPreviewPhoto?.title || 'Tagged photo'}
               </Typography>
               <IconButton aria-label='Close preview' onClick={() => setTagPreviewPhoto(null)} edge='end' size='small'>
                  <CloseIcon />
               </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0, bgcolor: 'common.black', position: 'relative' }}>
               {tagPreviewPhoto &&
                  (isSharedVideoPhoto(tagPreviewPhoto) ? (
                     <Box
                        component='video'
                        src={videoPlaybackSrc(tagPreviewPhoto)}
                        controls
                        playsInline
                        sx={{
                           width: '100%',
                           maxHeight: { xs: '75vh', sm: '85vh' },
                           objectFit: 'contain',
                           display: 'block',
                        }}
                     />
                  ) : (
                     <Box
                        component='img'
                        src={tagPreviewPhoto.url || tagPreviewPhoto.thumbnailUrl || ''}
                        alt={tagPreviewPhoto.title || ''}
                        sx={{
                           width: '100%',
                           height: 'auto',
                           maxHeight: { xs: '75vh', sm: '85vh' },
                           objectFit: 'contain',
                           display: 'block',
                        }}
                     />
                  ))}
            </DialogContent>
         </Dialog>

         <WetrippenAuthPromoDialog
            open={authDialogOpen}
            onClose={() => setAuthDialogOpen(false)}
            onLogin={onNavigateLogin}
            onGetStarted={onNavigateRegister}
            context='Sign in to leave a comment'
         />
      </Box>
   );
};

export default SharedCollectionPublicCommentsPanel;
